import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Plus, Trash2, Edit2, UserCheck } from 'lucide-react'

import Input from '../components/Input'
import Button from '../components/Button'
import Loading from '../components/Loading'
import DataTable from '../components/DataTable'
import Select from '../components/Select'
import { getCheckers, createChecker, updateChecker, deleteChecker, assignEvents } from '../actions/checkerActions'
import { getRoles } from '../actions/roleActions'
import { listEvents } from '../actions/eventActions'
import {
  GET_CHECKERS_RESET,
  CREATE_CHECKER_RESET,
  UPDATE_CHECKER_RESET,
  DELETE_CHECKER_RESET,
  ASSIGN_EVENTS_RESET,
} from '../constants/checkerConstants'

const CheckerManagement = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [rolesFromServer, setRolesFromServer] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [eventsFromServer, setEventsFromServer] = useState([])
  const [eventOptions, setEventOptions] = useState([])

  // Create Checker Modal States
  const [createModal, setCreateModal] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState('')
  const [assignedEvents, setAssignedEvents] = useState([])

  // Update Checker Modal States
  const [updateModal, setUpdateModal] = useState(false)
  const [updateId, setUpdateId] = useState('')
  const [updateName, setUpdateName] = useState('')
  const [updateEmail, setUpdateEmail] = useState('')
  const [updateRole, setUpdateRole] = useState('')

  // Delete Checker Modal States
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState('')
  const [deleteName, setDeleteName] = useState('')

  // Assign Events Modal States
  const [assignModal, setAssignModal] = useState(false)
  const [assignCheckerId, setAssignCheckerId] = useState('')
  const [assignCheckerName, setAssignCheckerName] = useState('')
  const [selectedEvents, setSelectedEvents] = useState([])

  // Loading
  const [loading, setLoading] = useState(true)

  const dispatch = useDispatch()

  // Check for auth
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  useEffect(() => {
    if (!userInfo) {
      navigate('/')
    }
  }, [userInfo, navigate])

  // Check for role permissions
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { getRoleData } = getRoleInfo

  useEffect(() => {
    if (getRoleData) {
      if (!getRoleData.checkersManage) {
        toast('Not Authorized to manage checkers', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
        navigate('/')
      }
    }
  }, [getRoleData, navigate])

  // Fetch initial data
  useEffect(() => {
    dispatch(getCheckers())
    dispatch(getRoles())
    dispatch(listEvents())
  }, [dispatch])

  // Get Checkers
  const getCheckersInfo = useSelector((state) => state.getCheckersInfo)
  const { errorGetCheckers, checkersData } = getCheckersInfo

  useEffect(() => {
    dispatch({ type: GET_CHECKERS_RESET })
    if (checkersData) {
      setLoading(false)
      if (Array.isArray(checkersData)) {
        setData(checkersData)
      } else if (checkersData.checkers && Array.isArray(checkersData.checkers)) {
        setData(checkersData.checkers)
      } else {
        setData([])
      }
    } else if (errorGetCheckers) {
      setLoading(false)
      toast(errorGetCheckers, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [checkersData, errorGetCheckers, dispatch])

  // Get Roles
  const getRolesInfo = useSelector((state) => state.getRolesInfo)
  const { getRolesData } = getRolesInfo

  useEffect(() => {
    if (getRolesData && Array.isArray(getRolesData)) {
      // Filter only checker and admin roles
      const checkerRoles = getRolesData.filter(
        (role) => role.name.toLowerCase() === 'checker' || role.name.toLowerCase() === 'admin'
      )
      setRolesFromServer(checkerRoles)
    }
  }, [getRolesData])

  useEffect(() => {
    if (Array.isArray(rolesFromServer) && rolesFromServer.length > 0) {
      const data = rolesFromServer.map((item) => ({
        id: item._id,
        title: item.name,
      }))
      setRoleOptions(data)
    }
  }, [rolesFromServer])

  // Get Events
  const eventList = useSelector((state) => state.eventList)
  const { events } = eventList

  useEffect(() => {
    if (events && Array.isArray(events)) {
      setEventsFromServer(events)
    } else if (events && events.events && Array.isArray(events.events)) {
      setEventsFromServer(events.events)
    }
  }, [events])

  useEffect(() => {
    if (Array.isArray(eventsFromServer) && eventsFromServer.length > 0) {
      const data = eventsFromServer.map((item) => ({
        id: item._id,
        title: item.name,
      }))
      setEventOptions(data)
    }
  }, [eventsFromServer])

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className='font-bold text-md text-green-800'>
            {row.original.name}
          </span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'role.name',
        header: 'Role',
        cell: ({ row }) => row.original.role?.name || 'N/A',
      },
      {
        accessorKey: 'assignedEvents',
        header: 'Assigned Events',
        cell: ({ row }) => {
          const count = row.original.assignedEvents?.length || 0
          return (
            <span className={`px-2 py-1 rounded text-xs font-medium ${count > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
              }`}>
              {count} {count === 1 ? 'Event' : 'Events'}
            </span>
          )
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex gap-2'>
            <button
              onClick={() => openAssignEventsModal(row.original)}
              className='bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded text-xs flex items-center gap-1'
              title='Assign Events'
            >
              <UserCheck size={14} />
              Assign
            </button>
            <button
              onClick={() => openUpdateModal(row.original)}
              className='bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-1 px-2 rounded text-xs flex items-center gap-1'
              title='Edit'
            >
              <Edit2 size={14} />
              Edit
            </button>
            <button
              onClick={() => openDeleteModal(row.original)}
              className='bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-xs flex items-center gap-1'
              title='Delete'
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        ),
        enableSorting: false,
      },
    ],
    []
  )

  // Create Checker Modal Functions
  const openCreateModal = () => {
    setCreateModal(true)
  }

  const closeCreateModal = () => {
    setCreateModal(false)
    setName('')
    setEmail('')
    setPassword('')
    setRole('')
    setAssignedEvents([])
  }

  const handleCreateChecker = () => {
    if (!name || !email || !password || !role) {
      toast('Name, Email, Password, and Role are mandatory', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toast('Please enter a valid email address', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    // Validate password length
    if (password.length < 6) {
      toast('Password must be at least 6 characters', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    dispatch(createChecker(name, email, password, role, assignedEvents))
  }

  // Update Checker Modal Functions
  const openUpdateModal = (checker) => {
    setUpdateId(checker._id)
    setUpdateName(checker.name)
    setUpdateEmail(checker.email)
    setUpdateRole(checker.role?._id || '')
    setUpdateModal(true)
  }

  const closeUpdateModal = () => {
    setUpdateModal(false)
    setUpdateId('')
    setUpdateName('')
    setUpdateEmail('')
    setUpdateRole('')
  }

  const handleUpdateChecker = () => {
    if (!updateName || !updateEmail || !updateRole) {
      toast('Name, Email, and Role are mandatory', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    dispatch(updateChecker(updateId, updateName, updateEmail, updateRole))
  }

  // Delete Checker Modal Functions
  const openDeleteModal = (checker) => {
    setDeleteId(checker._id)
    setDeleteName(checker.name)
    setDeleteModal(true)
  }

  const closeDeleteModal = () => {
    setDeleteModal(false)
    setDeleteId('')
    setDeleteName('')
  }

  const handleDeleteChecker = () => {
    dispatch(deleteChecker(deleteId))
  }

  // Assign Events Modal Functions
  const openAssignEventsModal = (checker) => {
    setAssignCheckerId(checker._id)
    setAssignCheckerName(checker.name)
    setSelectedEvents(checker.assignedEvents?.map(e => e._id || e) || [])
    setAssignModal(true)
  }

  const closeAssignEventsModal = () => {
    setAssignModal(false)
    setAssignCheckerId('')
    setAssignCheckerName('')
    setSelectedEvents([])
  }

  const handleAssignEvents = () => {
    dispatch(assignEvents(assignCheckerId, selectedEvents))
  }

  const toggleEventSelection = (eventId) => {
    setSelectedEvents(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId)
      } else {
        return [...prev, eventId]
      }
    })
  }

  // Create Checker Response
  const createCheckerInfo = useSelector((state) => state.createCheckerInfo)
  const { loadingCreateChecker, errorCreateChecker, createCheckerData } = createCheckerInfo

  useEffect(() => {
    dispatch({ type: CREATE_CHECKER_RESET })
    if (createCheckerData) {
      toast('Checker created successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      closeCreateModal()
      setTimeout(() => {
        dispatch(getCheckers())
      }, 1000)
    } else if (errorCreateChecker) {
      toast(errorCreateChecker, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [createCheckerData, errorCreateChecker, dispatch])

  // Update Checker Response
  const updateCheckerInfo = useSelector((state) => state.updateCheckerInfo)
  const { loadingUpdateChecker, errorUpdateChecker, updateCheckerData } = updateCheckerInfo

  useEffect(() => {
    dispatch({ type: UPDATE_CHECKER_RESET })
    if (updateCheckerData) {
      toast('Checker updated successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      closeUpdateModal()
      setTimeout(() => {
        dispatch(getCheckers())
      }, 1000)
    } else if (errorUpdateChecker) {
      toast(errorUpdateChecker, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [updateCheckerData, errorUpdateChecker, dispatch])

  // Delete Checker Response
  const deleteCheckerInfo = useSelector((state) => state.deleteCheckerInfo)
  const { loadingDeleteChecker, errorDeleteChecker, deleteCheckerData } = deleteCheckerInfo

  useEffect(() => {
    dispatch({ type: DELETE_CHECKER_RESET })
    if (deleteCheckerData) {
      toast('Checker deleted successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      closeDeleteModal()
      setTimeout(() => {
        dispatch(getCheckers())
      }, 1000)
    } else if (errorDeleteChecker) {
      toast(errorDeleteChecker, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [deleteCheckerData, errorDeleteChecker, dispatch])

  // Assign Events Response
  const assignEventsInfo = useSelector((state) => state.assignEventsInfo)
  const { loadingAssignEvents, errorAssignEvents, assignEventsData } = assignEventsInfo

  useEffect(() => {
    dispatch({ type: ASSIGN_EVENTS_RESET })
    if (assignEventsData) {
      toast('Events assigned successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      closeAssignEventsModal()
      setTimeout(() => {
        dispatch(getCheckers())
      }, 1000)
    } else if (errorAssignEvents) {
      toast(errorAssignEvents, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [assignEventsData, errorAssignEvents, dispatch])

  if (loading) {
    return <Loading />
  }

  return (
    <div className='w-full h-full'>
      <h1 className='text-2xl font-semibold mb-4'>Checker Management</h1>

      <DataTable
        data={data}
        columns={columns}
        title="Ticket Checkers"
        isLoading={loading}
        toolbar={
          <Button
            type='button'
            onClick={openCreateModal}
            text={<span className="flex items-center gap-2"><Plus size={16} />Create Checker</span>}
            className='bg-emerald-500 hover:bg-emerald-700'
          />
        }
      />

      {/* Create Checker Modal */}
      {createModal && (
        <>
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto min-h-screen">
            <div className="relative w-full max-w-xl mx-auto bg-white rounded-lg shadow-lg">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Create Checker Account</h3>
              </div>
              <div className="px-4 py-4">
                <Input
                  width="w-full mb-4"
                  name="Name *"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <Input
                  width="w-full mb-4"
                  name="Email *"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Input
                  width="w-full mb-4"
                  name="Password *"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Select
                  width="w-full mb-4"
                  name="Role *"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  options={roleOptions}
                />
                <p className="text-xs text-gray-500 mt-2">
                  All fields with * are mandatory. Events can be assigned after creation.
                </p>
              </div>
              <div className="flex justify-end px-4 py-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="text-red-500 font-bold uppercase text-sm px-4 py-2 mr-2"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleCreateChecker}
                  disabled={loadingCreateChecker}
                  className={`${loadingCreateChecker ? 'bg-gray-300' : 'bg-emerald-500 hover:bg-emerald-600'
                    } text-white font-bold uppercase text-sm px-6 py-2 rounded shadow`}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Update Checker Modal */}
      {updateModal && (
        <>
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto min-h-screen">
            <div className="relative w-full max-w-xl mx-auto bg-white rounded-lg shadow-lg">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Update Checker Account</h3>
              </div>
              <div className="px-4 py-4">
                <Input
                  width="w-full mb-4"
                  name="Name *"
                  value={updateName}
                  onChange={(e) => setUpdateName(e.target.value)}
                />
                <Input
                  width="w-full mb-4"
                  name="Email *"
                  type="email"
                  value={updateEmail}
                  onChange={(e) => setUpdateEmail(e.target.value)}
                />
                <Select
                  width="w-full mb-4"
                  name="Role *"
                  value={updateRole}
                  onChange={(e) => setUpdateRole(e.target.value)}
                  options={roleOptions}
                />
                <p className="text-xs text-gray-500 mt-2">
                  All fields with * are mandatory
                </p>
              </div>
              <div className="flex justify-end px-4 py-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeUpdateModal}
                  className="text-red-500 font-bold uppercase text-sm px-4 py-2 mr-2"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleUpdateChecker}
                  disabled={loadingUpdateChecker}
                  className={`${loadingUpdateChecker ? 'bg-gray-300' : 'bg-yellow-500 hover:bg-yellow-600'
                    } text-white font-bold uppercase text-sm px-6 py-2 rounded shadow`}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Checker Modal */}
      {deleteModal && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-md'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0'>Delete Checker</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed'>
                    Are you sure you want to delete <strong>{deleteName}</strong>?
                  </p>
                  <p className='text-gray-400 text-xs mt-2'>
                    This action cannot be undone and will remove all event assignments.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleDeleteChecker}
                    disabled={loadingDeleteChecker}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* Assign Events Modal */}
      {assignModal && (
        <>
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto min-h-screen">
            <div className="relative w-full max-w-2xl mx-auto bg-white rounded-lg shadow-lg">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Assign Events to {assignCheckerName}</h3>
              </div>
              <div className="px-4 py-4 max-h-96 overflow-y-auto">
                {eventsFromServer.length === 0 ? (
                  <p className="text-gray-500 text-sm">No events available to assign</p>
                ) : (
                  <div className="space-y-2">
                    {eventsFromServer.map((event) => (
                      <label key={event._id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedEvents.includes(event._id)}
                          onChange={() => toggleEventSelection(event._id)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{event.name}</p>
                          <p className="text-xs text-gray-500">
                            {event.location} - {event.startAt ? (() => {
                              const dateObj = new Date(event.startAt);
                              return !isNaN(dateObj.getTime())
                                ? dateObj.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                                : 'Date TBD';
                            })() : 'Date TBD'}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-4">
                  Selected: {selectedEvents.length} {selectedEvents.length === 1 ? 'event' : 'events'}
                </p>
              </div>
              <div className="flex justify-end px-4 py-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeAssignEventsModal}
                  className="text-red-500 font-bold uppercase text-sm px-4 py-2 mr-2"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleAssignEvents}
                  disabled={loadingAssignEvents}
                  className={`${loadingAssignEvents ? 'bg-gray-300' : 'bg-blue-500 hover:bg-blue-600'
                    } text-white font-bold uppercase text-sm px-6 py-2 rounded shadow`}
                >
                  Assign
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default CheckerManagement
